import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Button, Typography, message, Card, Table, Tag, Space,
  Divider, Select, Row, Col, Progress, DatePicker, Statistic,
  Tooltip, Popconfirm,
} from "antd";
import {
  FileExcelOutlined, FilterOutlined, ClearOutlined,
  DownloadOutlined, ReloadOutlined, BarChartOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/Logo_Prosperidad_Social.png";
import {
  obtenerInformeEntregas,
  exportarInformeEntregas,
  normalizarInformeEntregas,
} from "../../services/informeGeneralEntregas/informeGeneralEntregasService";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtNum = (n) => Number(n ?? 0).toLocaleString("es-CO");

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE CAMPOS PARA LAS HOJAS "DETALLE ENTREGA 1/2" (encabezados agrupados)
// ─────────────────────────────────────────────────────────────────────────────
// AJUSTA AQUÍ los nombres de campo si tu backend devuelve otros distintos.
// Puedes usar un string (nombre directo de la propiedad) o una función
// (para campos calculados, como "Nombre" = nombres + apellidos).
// ═══════════════════════════════════════════════════════════════════════════

const CAMPOS_DETALLE = {
  id:                  "id",
  encuesta:            "encuesta",
  proyecto:            "proyecto",
  departamento:        "departamento",
  municipio:           "municipio",
  puntoEntrega:        "punto_entrega",
  documento:           "numero_documento_titular",
  nombre:              "nombre_titular",
  documentoAutorizado: "numero_documento_aut",
  nombreAutorizado:    "nombre_completo_aut",
  quienReclama:        "quien_reclama_beneficio",
  fecha:               "fecha_diligenciamiento",
  hora:                "horafinaliza",
  estado:              "estado",
};

const getValor = (fila, campo) =>
  typeof campo === "function" ? campo(fila) : (fila[campo] ?? "");

// Cada valor se fuerza a String para evitar errores como
// "x.localeCompare is not a function" cuando el backend envía números.
const getValorTexto = (fila, campo) => String(getValor(fila, campo) ?? "").trim();

// Definición de los 4 grupos, sus colores y las columnas que contiene cada uno.
// El orden acá define el orden final de columnas en el Excel.
const GRUPOS_DETALLE = [
  {
    titulo: "Información General",
    colorGrupo: "FF1B5E20",   // verde oscuro
    colorHeader: "FF2E7D32",  // verde
    columnas: [
      { label: "ID",            campo: CAMPOS_DETALLE.id },
      { label: "Encuesta",      campo: CAMPOS_DETALLE.encuesta },
      { label: "Proyecto",      campo: CAMPOS_DETALLE.proyecto },
      { label: "Departamento",  campo: CAMPOS_DETALLE.departamento },
      { label: "Municipio",     campo: CAMPOS_DETALLE.municipio },
      { label: "Punto entrega", campo: CAMPOS_DETALLE.puntoEntrega },
    ],
  },
  {
    titulo: "Titular",
    colorGrupo: "FF0D47A1",   // azul oscuro
    colorHeader: "FF1565C0",  // azul
    columnas: [
      { label: "Documento", campo: CAMPOS_DETALLE.documento },
      { label: "Nombre Titular",    campo: CAMPOS_DETALLE.nombre },
    ],
  },
  {
    titulo: "Autorizado",
    colorGrupo: "FFE65100",   // naranja oscuro
    colorHeader: "FFEF6C00",  // naranja
    columnas: [
      { label: "Documento autorizado", campo: CAMPOS_DETALLE.documentoAutorizado },
      { label: "Nombre autorizado",    campo: CAMPOS_DETALLE.nombreAutorizado },
    ],
  },
  {
    titulo: "Control",
    colorGrupo: "FF4A148C",   // morado oscuro
    colorHeader: "FF6A1B9A",  // morado
    columnas: [
      { label: "Quién reclama", campo: CAMPOS_DETALLE.quienReclama },
      { label: "Fecha de diligenciamiento",         campo: CAMPOS_DETALLE.fecha },
      { label: "Hora finalizada",          campo: CAMPOS_DETALLE.hora },
      { label: "Estado",        campo: CAMPOS_DETALLE.estado },
    ],
  },
];

const TOTAL_COLUMNAS_DETALLE = GRUPOS_DETALLE.reduce((s, g) => s + g.columnas.length, 0);

// ─── Componente principal ──────────────────────────────────────────────────────

const InformeGeneralEntregas = () => {

  // ── Auth / Rol ─────────────────────────────────────────────────────────────
  const usernameFromState = useSelector((state) => state.auth.user);
  const esAdmin           = usernameFromState?.role === "1";
  const esCoordinador     = usernameFromState?.role === "2";

  // ── Datos ──────────────────────────────────────────────────────────────────
  const [consolidado, setConsolidado] = useState([]);
  const [loading,     setLoading]     = useState(false);

  // ── Filtros de fecha ───────────────────────────────────────────────────────
  const [rangoFechas, setRangoFechas] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin,    setFechaFin]    = useState(null);

  // ── Filtro de municipio (solo admin, filtro local) ─────────────────────────
  const [filtroMunicipio, setFiltroMunicipio] = useState(null);

  // ── Excel ──────────────────────────────────────────────────────────────────
  const [descargando,      setDescargando]      = useState(false);
  const [progresoDescarga, setProgresoDescarga] = useState(0);

  // ── Normalización de códigos DANE ──────────────────────────────────────────
  const [normalizando, setNormalizando] = useState(false);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    cargarInforme();
  }, []);

  // ── Carga del informe ──────────────────────────────────────────────────────
  const cargarInforme = useCallback(async (inicio = null, fin = null) => {
    setLoading(true);
    try {
      const codMunicipio = esCoordinador ? usernameFromState?.cod_municipio : null;
      const resultado    = await obtenerInformeEntregas(inicio, fin, codMunicipio);
      if (resultado?.success) {
        setConsolidado(resultado.data ?? []);
        if ((resultado.data ?? []).length === 0) {
          message.warning("No se encontraron registros para el período seleccionado");
        } else {
          message.success(`${resultado.data.length} municipios cargados`);
        }
      }
    } catch (error) {
      console.error(error);
      message.error("Error al consultar el informe de entregas");
    } finally {
      setLoading(false);
    }
  }, [esCoordinador, usernameFromState]);

  // ── Manejo de rango de fechas ──────────────────────────────────────────────
  const handleRangoChange = (dates) => {
    setRangoFechas(dates);
    if (dates && dates[0] && dates[1]) {
      setFechaInicio(dates[0].format("YYYY-MM-DD"));
      setFechaFin(dates[1].format("YYYY-MM-DD"));
    } else {
      setFechaInicio(null);
      setFechaFin(null);
    }
  };

  const aplicarFiltroFecha = () => {
    setFiltroMunicipio(null);
    cargarInforme(fechaInicio, fechaFin);
  };

  const limpiarFiltros = () => {
    setRangoFechas(null);
    setFechaInicio(null);
    setFechaFin(null);
    setFiltroMunicipio(null);
    cargarInforme();
  };

  const hayFiltrosFecha = !!fechaInicio || !!fechaFin;

  // ── Normalización de códigos DANE (municipio/departamento) ──────────────────
  const normalizarDatos = async () => {
    setNormalizando(true);
    try {
      const resultado = await normalizarInformeEntregas();
      if (resultado?.success) {
        message.success(
          `Normalización aplicada (${resultado.affectedRows ?? 0} filas actualizadas)`
        );
        cargarInforme(fechaInicio, fechaFin);
      } else {
        message.error("No se pudo completar la normalización");
      }
    } catch (error) {
      console.error(error);
      message.error("Error al normalizar los datos");
    } finally {
      setNormalizando(false);
    }
  };

  // ── Opciones municipio para el selector (solo admin) ──────────────────────
  const opcionesMunicipios = useMemo(() =>
    [...new Set(consolidado.map((r) => r.nombre_municipio).filter(Boolean))]
      .sort()
      .map((m) => ({ value: m, label: m })),
    [consolidado]
  );

  // ── Datos filtrados localmente por municipio (solo admin) ──────────────────
  const dataFiltrada = useMemo(() => {
    if (!filtroMunicipio) return consolidado;
    return consolidado.filter((r) => r.nombre_municipio === filtroMunicipio);
  }, [consolidado, filtroMunicipio]);

  // ── Totales ────────────────────────────────────────────────────────────────
  const totales = useMemo(() => ({
    entrega1: dataFiltrada.reduce((s, r) => s + Number(r.total_entrega_1 ?? 0), 0),
    entrega2: dataFiltrada.reduce((s, r) => s + Number(r.total_entrega_2 ?? 0), 0),
    entrega3: dataFiltrada.reduce((s, r) => s + Number(r.total_entrega_3 ?? 0), 0),
    general:  dataFiltrada.reduce((s, r) => s + Number(r.total_general   ?? 0), 0),
  }), [dataFiltrada]);

  // ── Excel: helpers generales ────────────────────────────────────────────────

  const numToColLetter = (n) => {
    let result = "";
    while (n > 0) {
      const rem = (n - 1) % 26;
      result = String.fromCharCode(65 + rem) + result;
      n = Math.floor((n - 1) / 26);
    }
    return result || "A";
  };

  const aplicarEncabezadoSheet = (workbook, worksheet, titulo, totalColumnas, logoImageId) => {
    const colFin = numToColLetter(Math.max(totalColumnas, 4));
    if (logoImageId !== null) {
      worksheet.mergeCells("A1:B3");
      worksheet.addImage(logoImageId, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    }
    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell     = worksheet.getCell("C1");
    titleCell.value     = titulo;
    titleCell.font      = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell     = worksheet.getCell("C3");
    dateCell.value     = `Fecha de exportación: ${new Date().toLocaleDateString("es-CO")}`;
    dateCell.alignment = { horizontal: "center" };
    worksheet.addRow([]);
  };

  const agregarTablaSheet = (worksheet, filas, headerColor = "FF217346") => {
    if (!filas.length) return;
    const headers = Object.keys(filas[0]);
    const headersCapitalizados = headers.map((h) => h.charAt(0).toUpperCase() + h.slice(1));
    const headerRow = worksheet.addRow(headersCapitalizados);
    headerRow.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: headerColor } };
      cell.alignment = { horizontal: "center" };
      cell.border    = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
    });
    filas.forEach((fila) => {
      const row = worksheet.addRow(Object.values(fila));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns = headers.map((h) => ({
      width: Math.max(h.length, ...filas.map((r) => String(r[h] ?? "").length)) + 4,
    }));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Tabla de RESUMEN (Departamento | Municipio | Titular | Autorizado | Total)
  // Se agrega AL FINAL de cada hoja de detalle (Entrega 1 y Entrega 2 por separado)
  // ═══════════════════════════════════════════════════════════════════════════

  const agregarResumenEnHoja = (worksheet, filas) => {
    // ── Agrupar por Departamento + Municipio ──────────────────────────────────
    const mapa = new Map();
    filas.forEach((fila) => {
      const departamento = getValorTexto(fila, CAMPOS_DETALLE.departamento) || "—";
      const municipio     = getValorTexto(fila, CAMPOS_DETALLE.municipio) || "—";
      const clave          = `${departamento}|||${municipio}`;
      const quienReclama   = getValorTexto(fila, CAMPOS_DETALLE.quienReclama).toLowerCase();

      if (!mapa.has(clave)) {
        mapa.set(clave, { departamento, municipio, titular: 0, autorizado: 0 });
      }
      const registro = mapa.get(clave);
      if (quienReclama === "autorizado") registro.autorizado++;
      else registro.titular++;
    });

    const filasResumen = [...mapa.values()].sort((a, b) =>
      a.departamento.localeCompare(b.departamento) || a.municipio.localeCompare(b.municipio)
    );

    // ── Dejar 2 filas de espacio antes del resumen ────────────────────────────
    worksheet.addRow([]);
    worksheet.addRow([]);

    // ── Título del bloque ──────────────────────────────────────────────────────
    const tituloRowNum = worksheet.rowCount + 1;
    worksheet.mergeCells(tituloRowNum, 1, tituloRowNum, 5);
    const tituloCell = worksheet.getCell(tituloRowNum, 1);
    tituloCell.value      = "RESUMEN POR UBICACIÓN";
    tituloCell.font       = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    tituloCell.fill       = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
    tituloCell.alignment  = { horizontal: "center", vertical: "middle" };

    // ── Encabezados ────────────────────────────────────────────────────────────
    const headerRow = worksheet.addRow(["Departamento", "Municipio", "Titular", "Autorizado", "Total"]);
    const coloresHeader = ["FF1B5E20", "FF1B5E20", "FF1565C0", "FFEF6C00", "FF4A148C"];
    headerRow.eachCell((cell, colNumber) => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: coloresHeader[colNumber - 1] } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border    = {
        top: { style: "thin" }, bottom: { style: "thin" },
        left: { style: "thin" }, right: { style: "thin" },
      };
    });

    // ── Filas de datos ─────────────────────────────────────────────────────────
    let totalTitular = 0;
    let totalAutorizado = 0;

    filasResumen.forEach((r) => {
      const total = r.titular + r.autorizado;
      totalTitular += r.titular;
      totalAutorizado += r.autorizado;

      const row = worksheet.addRow([r.departamento, r.municipio, r.titular, r.autorizado, total]);
      row.eachCell((cell, colNumber) => {
        cell.border    = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
        cell.alignment = colNumber >= 3 ? { horizontal: "center" } : { horizontal: "left" };
      });
    });

    // ── Fila de totales ─────────────────────────────────────────────────────────
    const totalGeneral = totalTitular + totalAutorizado;
    const totalRow = worksheet.addRow([
      "TOTAL GENERAL", "", totalTitular, totalAutorizado, totalGeneral,
    ]);
    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 2);
    totalRow.eachCell((cell) => {
      cell.font      = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border    = {
        top: { style: "thin" }, bottom: { style: "double" },
        left: { style: "thin" }, right: { style: "thin" },
      };
    });

    // ── Asegurar ancho mínimo en las primeras 5 columnas para el resumen ───────
    const anchoMinDepto = Math.max(18, ...filasResumen.map((r) => r.departamento.length + 3));
    const anchoMinMunic = Math.max(18, ...filasResumen.map((r) => r.municipio.length + 3));
    const anchosMinimos = [anchoMinDepto, anchoMinMunic, 12, 14, 12];
    anchosMinimos.forEach((ancho, i) => {
      const col = worksheet.getColumn(i + 1);
      col.width = Math.max(col.width || 0, ancho);
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HOJA "Detalle Entrega N" — encabezados agrupados + resumen de totales
  // ═══════════════════════════════════════════════════════════════════════════

  const crearHojaDetalleEntrega = (workbook, filas, numeroEntrega, logoImageId) => {
    const worksheet = workbook.addWorksheet(`Detalle Entrega ${numeroEntrega}`);

    aplicarEncabezadoSheet(workbook, worksheet, `DETALLE ENTREGA ${numeroEntrega}`, TOTAL_COLUMNAS_DETALLE, logoImageId);

    // ── Fila de grupos (mergeada) ─────────────────────────────────────────────
    const groupRow = worksheet.rowCount + 1;
    let colCursor  = 1;

    GRUPOS_DETALLE.forEach((grupo) => {
      const inicio = colCursor;
      const fin    = colCursor + grupo.columnas.length - 1;

      worksheet.mergeCells(groupRow, inicio, groupRow, fin);
      const cell = worksheet.getCell(groupRow, inicio);
      cell.value      = grupo.titulo.toUpperCase();
      cell.font       = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill       = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorGrupo } };
      cell.alignment  = { horizontal: "center", vertical: "middle" };

      for (let c = inicio; c <= fin; c++) {
        worksheet.getCell(groupRow, c).border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      }
      colCursor = fin + 1;
    });
    worksheet.getRow(groupRow).height = 22;

    // ── Fila de encabezados de columna ────────────────────────────────────────
    const headerRow = groupRow + 1;
    colCursor = 1;
    GRUPOS_DETALLE.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const cell = worksheet.getCell(headerRow, colCursor);
        cell.value      = col.label;
        cell.font       = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill       = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorHeader } };
        cell.alignment  = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border     = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
        colCursor++;
      });
    });
    worksheet.getRow(headerRow).height = 28;

    // ── Filas de datos ─────────────────────────────────────────────────────────
    filas.forEach((fila) => {
      const valores = [];
      GRUPOS_DETALLE.forEach((grupo) =>
        grupo.columnas.forEach((col) => valores.push(getValor(fila, col.campo)))
      );
      const row = worksheet.addRow(valores);
      row.eachCell((cell) => {
        cell.border    = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle" };
      });
    });

    // ── Anchos de columna ──────────────────────────────────────────────────────
    colCursor = 1;
    GRUPOS_DETALLE.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const valores = filas.map((f) => String(getValor(f, col.campo) ?? ""));
        const ancho   = Math.max(col.label.length, ...valores.map((v) => v.length), 10) + 3;
        worksheet.getColumn(colCursor).width = ancho;
        colCursor++;
      });
    });

    // ── Fijar encabezados al hacer scroll ──────────────────────────────────────
    worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: headerRow, showGridLines: true }];

    // ── Resumen de totales al final de esta misma hoja ─────────────────────────
    agregarResumenEnHoja(worksheet, filas);

    return worksheet;
  };

  // ── Descarga de Excel ────────────────────────────────────────────────────────

  const descargarExcel = async () => {
    if (!dataFiltrada.length) { message.warning("No hay datos para exportar"); return; }

    setDescargando(true);
    setProgresoDescarga(10);

    try {
      // ── Logo (una sola vez) ────────────────────────────────────────────────
      const workbook = new ExcelJS.Workbook();
      let logoImageId = null;
      try {
        const response = await fetch(logo);
        const buffer   = await response.arrayBuffer();
        logoImageId = workbook.addImage({ buffer, extension: "png" });
      } catch { console.warn("Logo no disponible, se omitirá en el Excel"); }

      // ── Obtener detalle filtrado ───────────────────────────────────────────
      let entrega1Rows = [];
      let entrega2Rows = [];
      let entrega3Rows = [];
      try {
        setProgresoDescarga(30);

        // Prioridad de filtro para el detalle:
        // 1. Coordinador  → filtra por su OID (cod_municipio)
        // 2. Admin con selector de municipio activo → filtra por nombre exacto
        // 3. Sin filtro   → trae todos los municipios
        const codMunicipioExport    = esCoordinador
          ? (usernameFromState?.cod_municipio ?? null)
          : null;
        const nombreMunicipioExport = !esCoordinador && filtroMunicipio
          ? filtroMunicipio
          : null;

        const exportResult = await exportarInformeEntregas(
          fechaInicio,
          fechaFin,
          codMunicipioExport,
          nombreMunicipioExport
        );

        if (exportResult?.success) {
          entrega1Rows = exportResult.data?.entrega1 ?? [];
          entrega2Rows = exportResult.data?.entrega2 ?? [];
          entrega3Rows = exportResult.data?.entrega3 ?? [];
        }
      } catch (e) {
        console.warn("No se pudo obtener detalle, exportando solo consolidado", e);
      }

      setProgresoDescarga(55);

      // ── Hoja 1: Consolidado (SIN CAMBIOS) ─────────────────────────────────
      const wsConsolidado = workbook.addWorksheet("Consolidado por Municipio");
      aplicarEncabezadoSheet(workbook, wsConsolidado, "INFORME GENERAL DE ENTREGAS — CONSOLIDADO", 5, logoImageId);
      const filasConsolidado = dataFiltrada.map((r) => ({
        "Municipio":     r.nombre_municipio ?? "—",
        "Entrega 1":     Number(r.total_entrega_1 ?? 0),
        "Entrega 2":     Number(r.total_entrega_2 ?? 0),
        "Entrega 3":     Number(r.total_entrega_3 ?? 0),
        "Total General": Number(r.total_general   ?? 0),
      }));
      agregarTablaSheet(wsConsolidado, filasConsolidado, "FF1B5E20");

      const totalRow = wsConsolidado.addRow([
        "TOTAL GENERAL", totales.entrega1, totales.entrega2, totales.entrega3, totales.general,
      ]);
      totalRow.eachCell((cell) => {
        cell.font   = { bold: true };
        cell.fill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F5E9" } };
        cell.border = { top: { style: "thin" }, bottom: { style: "double" }, left: { style: "thin" }, right: { style: "thin" } };
      });

      setProgresoDescarga(70);

      // ── Hoja 2: Detalle Entrega 1 (nuevo diseño, con Autorizado) ───────────
      if (entrega1Rows.length) {
        crearHojaDetalleEntrega(workbook, entrega1Rows, 1, logoImageId);
      }

      setProgresoDescarga(85);

      // ── Hoja 3: Detalle Entrega 2 (nuevo diseño, con Autorizado) ───────────
      if (entrega2Rows.length) {
        crearHojaDetalleEntrega(workbook, entrega2Rows, 2, logoImageId);
      }

      setProgresoDescarga(95);

      // ── Hoja 4: Detalle Entrega 3 (nuevo diseño, con Autorizado) ───────────
      if (entrega3Rows.length) {
        crearHojaDetalleEntrega(workbook, entrega3Rows, 3, logoImageId);
      }

      setProgresoDescarga(98);

      const periodoLabel = fechaInicio && fechaFin ? `_${fechaInicio}_${fechaFin}` : "";
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `InformeEntregas${periodoLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      message.success(`Excel exportado con ${dataFiltrada.length} municipios`);
    } catch (error) {
      console.error(error);
      message.error("Error al generar el Excel");
    } finally {
      setDescargando(false);
      setProgresoDescarga(0);
    }
  };

  // ── Columnas tabla consolidado (pantalla) ───────────────────────────────────
  const columns = [
    {
      title: "Municipio",
      dataIndex: "nombre_municipio",
      key: "nombre_municipio",
      fixed: "left",
      width: 200,
      sorter: (a, b) => String(a.nombre_municipio).localeCompare(String(b.nombre_municipio)),
      render: (v) => v ?? "—",
    },
    {
      title: "Entrega 1",
      dataIndex: "total_entrega_1",
      key: "total_entrega_1",
      width: 130,
      align: "center",
      sorter: (a, b) => Number(a.total_entrega_1) - Number(b.total_entrega_1),
      render: (v) => (
        <Tag color="blue" style={{ minWidth: 50, textAlign: "center" }}>{fmtNum(v)}</Tag>
      ),
    },
    {
      title: "Entrega 2",
      dataIndex: "total_entrega_2",
      key: "total_entrega_2",
      width: 130,
      align: "center",
      sorter: (a, b) => Number(a.total_entrega_2) - Number(b.total_entrega_2),
      render: (v) => (
        <Tag color="purple" style={{ minWidth: 50, textAlign: "center" }}>{fmtNum(v)}</Tag>
      ),
    },
    {
      title: "Entrega 3",
      dataIndex: "total_entrega_3",
      key: "total_entrega_3",
      width: 130,
      align: "center",
      sorter: (a, b) => Number(a.total_entrega_3) - Number(b.total_entrega_3),
      render: (v) => (
        <Tag color="magenta" style={{ minWidth: 50, textAlign: "center" }}>{fmtNum(v)}</Tag>
      ),
    },
    {
      title: "Total General",
      dataIndex: "total_general",
      key: "total_general",
      width: 150,
      align: "center",
      sorter: (a, b) => Number(a.total_general) - Number(b.total_general),
      render: (v) => (
        <Tag color="success" style={{ minWidth: 50, textAlign: "center", fontWeight: 600 }}>{fmtNum(v)}</Tag>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Informe General de Entregas</Title>

      {/* ── Tarjetas de totales ────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: "4px solid #1677ff" }}>
            <Statistic title="Total Entrega 1" value={totales.entrega1} valueStyle={{ color: "#1677ff" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: "4px solid #722ed1" }}>
            <Statistic title="Total Entrega 2" value={totales.entrega2} valueStyle={{ color: "#722ed1" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: "4px solid #eb2f96" }}>
            <Statistic title="Total Entrega 3" value={totales.entrega3} valueStyle={{ color: "#eb2f96" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: "4px solid #52c41a" }}>
            <Statistic title="Total General" value={totales.general} valueStyle={{ color: "#52c41a", fontWeight: 700 }} />
          </Card>
        </Col>
      </Row>

      {/* ── Acciones ──────────────────────────────────────────────────────── */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Tooltip title={descargando ? "Generando Excel..." : `Exportar ${dataFiltrada.length} municipios`}>
          <Button
            icon={descargando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={descargarExcel}
            loading={descargando}
            disabled={descargando || dataFiltrada.length === 0}
            style={{ borderColor: "#217346", color: descargando ? undefined : "#217346" }}
          >
            {descargando ? `Descargando ${progresoDescarga}%` : "Descargar Excel"}
          </Button>
        </Tooltip>
        <Button
          icon={<ReloadOutlined />}
          onClick={limpiarFiltros}
          disabled={loading || descargando}
        >
          Recargar
        </Button>
        {esAdmin && (
          <Popconfirm
            title="Normalizar códigos DANE"
            description="Corrige el municipio de Paz de Ariporo y homogeniza los departamentos 05/08 en las 3 tablas de entregas. ¿Continuar?"
            okText="Sí, normalizar"
            cancelText="Cancelar"
            onConfirm={normalizarDatos}
            disabled={normalizando}
          >
            <Button
              icon={<ToolOutlined />}
              loading={normalizando}
              disabled={loading || descargando || normalizando}
            >
              Normalizar
            </Button>
          </Popconfirm>
        )}
      </Space>

      {/* ── Progreso descarga ──────────────────────────────────────────────── */}
      {descargando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoDescarga}
            status="active"
            strokeColor={{ "0%": "#217346", "100%": "#52c41a" }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel, no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Filtros de fecha ───────────────────────────────────────────────── */}
      <Divider orientation="left" style={{ margin: "12px 0" }}>
        <Space><FilterOutlined /> Filtrar por período{!esCoordinador && " o municipio"}</Space>
      </Divider>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} sm={14} md={10} lg={8}>
          <RangePicker
            style={{ width: "100%" }}
            value={rangoFechas}
            onChange={handleRangoChange}
            format="YYYY-MM-DD"
            placeholder={["Fecha inicio", "Fecha fin"]}
            disabled={loading || descargando}
            allowClear
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={aplicarFiltroFecha}
            disabled={loading || descargando || !hayFiltrosFecha}
            loading={loading}
          >
            Aplicar
          </Button>
        </Col>
        {(hayFiltrosFecha || filtroMunicipio) && (
          <Col>
            <Button
              icon={<ClearOutlined />}
              onClick={limpiarFiltros}
              disabled={loading || descargando}
            >
              Limpiar
            </Button>
          </Col>
        )}
      </Row>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      {consolidado.length > 0 && !loading && (
        <>
          {esAdmin && (
            <>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                Filtro de tabla
              </Divider>
              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Filtrar por municipio..."
                    style={{ width: "100%" }}
                    value={filtroMunicipio}
                    onChange={(v) => setFiltroMunicipio(v ?? null)}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={opcionesMunicipios}
                    suffixIcon={<BarChartOutlined />}
                    disabled={descargando}
                  />
                </Col>
              </Row>
            </>
          )}

          <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
            <Col>
              <Text type="secondary">
                Mostrando{" "}
                <Text strong>{dataFiltrada.length.toLocaleString("es-CO")}</Text>{" "}
                municipio{dataFiltrada.length !== 1 ? "s" : ""}
                {filtroMunicipio && <Text type="warning"> (filtro activo)</Text>}
                {hayFiltrosFecha && (
                  <Text type="secondary">
                    {" "}· período <Text strong>{fechaInicio}</Text>{" → "}<Text strong>{fechaFin}</Text>
                  </Text>
                )}
              </Text>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={dataFiltrada.map((r, i) => ({ ...r, key: r.nombre_municipio ?? i }))}
            rowKey="key"
            size="middle"
            bordered
            loading={loading}
            scroll={{ x: 830 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100"],
              showTotal: (total, range) => `${range[0]}–${range[1]} de ${total} registros`,
            }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} fixed="left">
                  <Text strong>Total general</Text>
                </Table.Summary.Cell>
                {[
                  ["blue",    totales.entrega1],
                  ["purple",  totales.entrega2],
                  ["magenta", totales.entrega3],
                  ["success", totales.general],
                ].map(([color, val], i) => (
                  <Table.Summary.Cell key={i} index={i + 1} align="center">
                    <Tag color={color} style={{ fontWeight: 600 }}>{fmtNum(val)}</Tag>
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            )}
          />
        </>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {consolidado.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <img
            src="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            alt="sin datos"
            style={{ width: 120, marginBottom: 16, opacity: 0.45 }}
          />
          <br />
          <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
            No se encontraron registros de entregas
          </Text>
        </div>
      )}
    </Card>
  );
};

export default InformeGeneralEntregas;